import React from 'react';
import { Box, Menu, Text, Tooltip, UnstyledButton, rem } from '@mantine/core';
import { motion, useReducedMotion } from 'framer-motion';
import { SPRING } from '@/theme/motion';
import { TOUCH_TARGET, useIsTouch } from '@/hooks/use.responsive';
import { navigationContains } from '@/function/navigation/navigation.find';
import navigationFlatten from '@/function/navigation/navigation.flatten';
import type { TypeNavNode } from '@/types/ui/types.ui.navigation';

type Props = {
    node: TypeNavNode;
    pathname: string;
    onNavigate: (node: TypeNavNode) => void;
    badge?: number;
};

const ShellNavbarCollapsedNode: React.FC<Props> = ({ node, pathname, onNavigate, badge = 0 }) => {
    const isTouch = useIsTouch();
    const reduced = useReducedMotion();
    const active = navigationContains(node, pathname);
    const hasChildren = Boolean(node.children?.length);
    const Icon = node.icon;

    const button = (
        <UnstyledButton
            onClick={() => onNavigate(node)}
            aria-label={node.label}
            aria-current={active ? 'page' : undefined}
            style={{
                position: 'relative',
                display: 'grid',
                placeItems: 'center',
                width: '100%',
                height: rem(isTouch ? TOUCH_TARGET : 40),
                borderRadius: 'var(--mantine-radius-md)',
                color: active ? 'var(--bd-brand-text)' : 'var(--mantine-color-text)',
                transition: 'color 140ms ease, background-color 140ms ease',
            }}
            onMouseEnter={(event) => { if (!active) event.currentTarget.style.backgroundColor = 'var(--bd-surface-hover)'; }}
            onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = ''; }}
        >
            {active ? (
                <motion.span
                    layoutId="navbar-active-item"
                    transition={reduced ? { duration: 0 } : SPRING}
                    aria-hidden
                    style={{ position: 'absolute', inset: 0, borderRadius: 'var(--mantine-radius-md)', backgroundColor: 'var(--bd-brand-soft)' }}
                />
            ) : null}
            {active ? (
                <motion.span
                    layoutId="navbar-active-bar"
                    transition={reduced ? { duration: 0 } : SPRING}
                    aria-hidden
                    style={{ position: 'absolute', left: 0, top: rem(9), bottom: rem(9), width: rem(3), borderRadius: rem(3), backgroundColor: 'var(--bd-brand-text)' }}
                />
            ) : null}
            <Icon style={{ position: 'relative', width: rem(21), height: rem(21) }} stroke={1.7} />
            {badge > 0 ? <span className="bd-nav-badge bd-nav-badge-dot" aria-label={`${badge} da leggere`}>{badge > 99 ? '99+' : badge}</span> : null}
            {hasChildren ? (
                <span
                    aria-hidden
                    style={{
                        position: 'absolute', right: rem(9), bottom: rem(9),
                        width: rem(5), height: rem(5), borderRadius: '50%',
                        backgroundColor: active ? 'var(--bd-brand-text)' : 'var(--bd-border-strong)',
                    }}
                />
            ) : null}
        </UnstyledButton>
    );

    if (!hasChildren || !node.children) {
        return <Tooltip label={node.label} position="right" offset={14}>{button}</Tooltip>;
    }

    const flat = navigationFlatten(node.children);

    return (
        <Menu trigger="click-hover" position="right-start" offset={12} width={240} withinPortal shadow="lg" openDelay={80} closeDelay={160}>
            <Menu.Target>{button}</Menu.Target>
            <Menu.Dropdown>
                <Menu.Label>
                    <Text size="xs" fw={650} style={{ color: 'var(--bd-text-strong)' }}>{node.label}</Text>
                </Menu.Label>
                {node.expandOnly ? null : (
                    <>
                        <Menu.Item onClick={() => onNavigate(node)} leftSection={<Icon size={16} stroke={1.7} />} fw={500}>
                            Apri {node.label}
                        </Menu.Item>
                        <Menu.Divider />
                    </>
                )}
                {flat.map(({ node: child, trail }) => {
                    const ChildIcon = child.icon;
                    const depth = trail.length - 1;
                    const childActive = pathname === child.path || pathname.startsWith(`${child.path}/`);

                    if (child.expandOnly) {
                        return (
                            <Menu.Label key={child.id}>
                                <Box pl={rem(depth * 12)} component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: rem(8) }}>
                                    <ChildIcon size={14} stroke={1.7} />
                                    <Text size="xs" fw={600}>{child.label}</Text>
                                </Box>
                            </Menu.Label>
                        );
                    }

                    return (
                        <Menu.Item
                            key={child.id}
                            onClick={() => onNavigate(child)}
                            leftSection={<Box pl={rem(depth * 12)} style={{ display: 'flex' }}><ChildIcon size={16} stroke={1.7} /></Box>}
                            fw={childActive ? 650 : 500}
                            style={childActive ? { color: 'var(--bd-brand-text)', backgroundColor: 'var(--bd-brand-soft)' } : undefined}
                        >
                            {child.label}
                        </Menu.Item>
                    );
                })}
            </Menu.Dropdown>
        </Menu>
    );
};

export default ShellNavbarCollapsedNode;
